import { useEffect, useState } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, Play, Mic, Check, Lightbulb } from "lucide-react";

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const ROLES = [
  "Chiropractors",
  "Physiotherapists",
  "Massage Therapists",
  "Athletic Therapists",
  "Rehabilitation Clinics",
];

function RotatingRole() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % ROLES.length), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <span className="block min-h-[1.15em]">
      <AnimatePresence mode="wait">
        <motion.span
          key={ROLES[i]}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent"
        >
          {ROLES[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

const waveBars = [14, 26, 38, 22, 30, 18, 34, 24, 40, 20, 28, 16];

const TRANSCRIPT = [
  { who: "Provider", text: "Where are you feeling the pain today?" },
  { who: "Patient", text: "Lower back, mostly when I bend forward…" },
];

const SOAP = [
  { k: "S", t: "Pt reports lower back pain, 6/10, worse with flexion." },
  { k: "O", t: "Lumbar ROM limited in flexion. Tenderness L4–L5." },
  { k: "A", t: "Mechanical low back pain, no red flags." },
  { k: "P", t: "Manual therapy + home exercise plan. Follow-up in 1 week." },
];

function getMockupDomain() {
  if (typeof window !== "undefined") {
    const host = window.location.host;
    const isLocal =
      !host ||
      host.includes("localhost") ||
      host.includes("127.0.0.1") ||
      host.includes("replit") ||
      host.includes("repl.co");
    if (!isLocal) return `${host}/visit`;
  }
  return "app.yourclinic.com/visit";
}

function AppMockup() {
  const [transcriptCount, setTranscriptCount] = useState(0);
  const [soapCount, setSoapCount] = useState(0);
  const [noteReady, setNoteReady] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);

  useEffect(() => {
    let timers: ReturnType<typeof setTimeout>[] = [];

    const run = () => {
      timers.forEach(clearTimeout);
      timers = [];

      setTranscriptCount(0);
      setSoapCount(0);
      setNoteReady(false);
      setShowSuggestion(false);

      timers.push(setTimeout(() => setTranscriptCount(1), 700));
      timers.push(setTimeout(() => setTranscriptCount(2), 1600));
      timers.push(setTimeout(() => setSoapCount(1), 2700));
      timers.push(setTimeout(() => setSoapCount(2), 3200));
      timers.push(setTimeout(() => setSoapCount(3), 3700));
      timers.push(
        setTimeout(() => {
          setSoapCount(4);
          setNoteReady(true);
        }, 4200),
      );
      timers.push(setTimeout(() => setShowSuggestion(true), 5100));
      timers.push(setTimeout(run, 8200));
    };

    run();
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      className="relative"
    >
      {/* Glow */}
      <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-tr from-blue-200/40 via-sky-100/40 to-transparent blur-2xl" />

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-2xl shadow-blue-900/10 ring-1 ring-black/[0.02]">
        {/* Window bar */}
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-red-400/80" />
          <span className="h-3 w-3 rounded-full bg-amber-400/80" />
          <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
          <div className="ml-3 flex items-center gap-2 rounded-md bg-white px-3 py-1 text-xs font-medium text-slate-400 ring-1 ring-slate-200">
            {getMockupDomain()}
          </div>
        </div>

        <div className="space-y-4 p-5">
          {/* Recording */}
          <div className="flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white">
                <Mic className="h-4 w-4" />
                <span className="absolute inset-0 animate-ping rounded-full bg-blue-600/30" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">Recording visit</p>
                <p className="text-xs text-slate-500">Live transcription on</p>
              </div>
            </div>
            <div className="flex h-9 items-end gap-[3px]">
              {waveBars.map((h, i) => (
                <motion.span
                  key={i}
                  className="w-[3px] rounded-full bg-blue-500"
                  animate={{ height: [6, h, 6] }}
                  transition={{
                    duration: 0.9,
                    repeat: Infinity,
                    delay: i * 0.07,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Transcript */}
          <div className="min-h-[88px] rounded-xl border border-slate-100 bg-white p-4">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Transcript
            </p>
            <div className="space-y-1.5">
              {TRANSCRIPT.slice(0, transcriptCount).map((line, idx) => (
                <motion.p
                  key={idx}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="text-xs leading-relaxed text-slate-600"
                >
                  <span className="font-semibold text-slate-800">{line.who}:</span> {line.text}
                </motion.p>
              ))}
              {transcriptCount === 0 && (
                <span className="text-xs italic text-slate-400">Listening…</span>
              )}
            </div>
          </div>

          {/* SOAP note */}
          <div className="rounded-xl border border-slate-100 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Generated SOAP note
              </p>
              {noteReady ? (
                <motion.span
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600"
                >
                  Ready
                </motion.span>
              ) : (
                <span className="flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                  Generating…
                </span>
              )}
            </div>
            <div className="min-h-[112px] space-y-2.5">
              {SOAP.slice(0, soapCount).map((row) => (
                <motion.div
                  key={row.k}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="flex gap-2.5"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-blue-600 text-[10px] font-bold text-white">
                    {row.k}
                  </span>
                  <p className="text-xs leading-snug text-slate-600">{row.t}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Suggestion */}
          <div className="min-h-[56px]">
            <AnimatePresence>
              {showSuggestion && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <Lightbulb className="h-4 w-4" />
                  </span>
                  <p className="text-xs text-slate-600">
                    <span className="font-semibold text-slate-800">Documentation suggestion:</span>{" "}
                    Add pain scale to strengthen this note.
                  </p>
                  <span className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

export function Hero() {
  return (
    <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      {/* Background accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/70 via-white to-white" />
        <div className="absolute -top-24 right-0 h-[28rem] w-[28rem] rounded-full bg-blue-200/30 blur-3xl" />
        <div className="absolute -left-24 top-40 h-[24rem] w-[24rem] rounded-full bg-sky-200/30 blur-3xl" />
      </div>

      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div initial="hidden" animate="visible">
          <motion.span
            custom={0}
            variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3.5 py-1.5 text-xs font-semibold text-blue-700"
          >
            <Sparkles className="h-3.5 w-3.5" />
            AI-Powered Clinical Documentation
          </motion.span>

          {/* Social proof badge */}
          <motion.p
            custom={1}
            variants={fadeUp}
            className="mt-5 flex items-center gap-2.5 text-xs font-medium text-slate-500"
          >
            <span className="flex -space-x-1.5">
              <span className="h-5 w-5 rounded-full border-2 border-white bg-blue-500" />
              <span className="h-5 w-5 rounded-full border-2 border-white bg-sky-400" />
              <span className="h-5 w-5 rounded-full border-2 border-white bg-emerald-400" />
            </span>
            Built for chiropractors, physiotherapists, massage therapists &amp; MSK clinics
          </motion.p>

          <motion.h1
            custom={2}
            variants={fadeUp}
            className="mt-6 text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            The AI Clinical Scribe Built for <RotatingRole />
          </motion.h1>

          <motion.p
            custom={3}
            variants={fadeUp}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
          >
            PalNote is the AI medical scribe that turns natural patient conversations into
            accurate, clinic-ready SOAP notes in seconds. Record, transcribe, and document every
            visit with voice AI—so you can spend more time with patients and less time charting.
          </motion.p>

          <motion.div
            custom={4}
            variants={fadeUp}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Link
              href="/sign-up"
              data-testid="button-hero-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/30 active:translate-y-0"
            >
              Start Free Trial
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <button
              onClick={() => scrollToId("how-it-works")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-7 py-3.5 text-base font-semibold text-slate-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50 active:translate-y-0"
            >
              <Play className="h-4 w-4 text-blue-600" />
              Watch Demo
            </button>
          </motion.div>

          <motion.p custom={5} variants={fadeUp} className="mt-6 text-sm text-slate-500">
            Clinical documentation, powered by conversation.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <AppMockup />
        </motion.div>
      </div>
    </section>
  );
}
